import { ScrollCaptain } from 'scroll-captain';
import { Video } from './../../components/video/video';
import './tab-content.scss';

class TabContent {
    constructor (element, options) {
        // Defaults & Settings
        const defaults = {
            initAttr: 'data-tabcontent',
            hiddenAttr: 'data-tabcontent-hidden',
            animateAttr: 'data-tabcontent-animate',
            pausedAttr: 'data-tabcontent-paused',
            themingAttr: 'data-tabcontent-theming',
            lightnessAttr: 'data-tabcontent-lightness',
            introValue: 'intro',
            textValue: 'text',
            circleValue: 'circle',
            stickyValue: 'sticky',
            tabpanelValue: 'tabpanel',
            videoValue: 'video',
            mediaControlValue: 'media-control',
            themingValue: 'theming',
            breakpoint: 767
        };

        this.settings = Object.assign({}, defaults, options);

        // Elements
        this.$root = element;
        this.$$tabPanels = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.tabpanelValue}"]`);
        this.$$videos = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.videoValue}"]`);
        this.$themingMarker = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.themingValue}"]`);

        // Properties
        this.hasIntro = !!this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.introValue}"]`);
        this.shouldPlay = true;
        this.activeIndex = 0;
        this.animation = null;
        this.breakpoint = window.matchMedia(`(max-width: ${this.settings.breakpoint}px)`);
        this.breakpointActive = this.breakpoint.matches;
    }

    /**
     * Initializes the tab content:
     * - Updates theming based on the first tab panel
     * - Prepares videos for playback
     * - Sets event listeners for tab and media control changes
     * - Loads intro animation if present
     */
    initialize () {
        if (this.$$tabPanels[0]) {
            this.updateTheming(this.$$tabPanels[0]);
        }

        this.prepareVideos();
        this.setEvents();

        if (this.hasIntro) {
            this.loadAnimation();
        }
    }

    /**
     * Lazy-loads videos when the root element enters the viewport:
     * - Loads the first video and starts playback
     * - Loads remaining videos in paused state
     * - Uses IntersectionObserver to delay loading until visible
     */
    prepareVideos () {
        const loadVideos = () => {
            const $firstVideo = this.$$videos[0];
            if (!$firstVideo) return;
            const firstVideoAPI = new Video($firstVideo);
            $firstVideo.videoAPI = firstVideoAPI;
            $firstVideo.videoAPI.loadVideo(() => {
                $firstVideo.videoAPI.toggleVideoState(true);

                const $$hiddenVideos = Array.from(this.$$videos).slice(1);
                $$hiddenVideos.forEach($hiddenVideo => {
                    const hiddenVideoAPI = new Video($hiddenVideo);
                    $hiddenVideo.videoAPI = hiddenVideoAPI;
                    $hiddenVideo.videoAPI.loadVideo(() => {
                        $hiddenVideo.videoAPI.toggleVideoState(false);
                    });
                });
            });
        };

        const observeVideos = () => {
            if (!this.$root) return;

            const observer = new IntersectionObserver((entries, observerInstance) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        loadVideos();
                        observerInstance.disconnect();
                    }
                });
            }, {
                root: null,
                rootMargin: '50% 0px',
                threshold: 0
            });

            observer.observe(this.$root);
        };

        observeVideos();
    }

    /**
     * Triggers scroll-based animations for the tab content intro section:
     * - Animates sticky elements and circles using ScrollCaptain
     * - Animates text movement on scroll
     * - Configurable via class settings (intro, sticky, circle, text)
     */
    loadAnimation () {
        this.animation = new ScrollCaptain(this.$root, {
            initAttr: this.settings.initAttr,
            triggerSuffix: this.settings.introValue,
            triggerPosition: 0.5,
            bottom: '-50%',
            top: this.breakpointActive ? `${window.innerHeight * 0.5 - this.$root.clientHeight * 0.25}px` : '-50%',
            cssSpace: 'sticky-spacing',
            animateSticky: {
                targetSuffix: this.settings.stickyValue,
                sticky: true
            },
            animateCircle: {
                targetSuffix: this.settings.circleValue,
                scale: window.reducedMotionActive ? {0: 0.2, 100: 0.2} : {0: 0.2, 100: 1},
                onscroll: true,
                duration: 0
            },
            updateOnResize: () => {
                if (this.breakpoint.matches !== this.breakpointActive) {
                    this.breakpointActive = this.breakpoint.matches;
                    this.updateAnimation(false);
                }
            }
        });
    }

    /**
     * Activates the selected tab panel and deactivates others:
     * - Sets `inert` and `aria-hidden` attributes for accessibility
     * - Updates CSS `--index` property for layout animations
     * - Shows/hides videos and controls playback
     * - Updates theming based on the active panel
     * @param {HTMLElement} $activeTabPanel
     */
    toggleTabPanels($activeTabPanel) {
        this.$$tabPanels?.forEach(($tabPanel, index) => {
            const isActive = $tabPanel === $activeTabPanel;
            $tabPanel.toggleAttribute('inert', !isActive);
            $tabPanel.setAttribute('aria-hidden', !isActive);

            if (index !== this.activeIndex) {
                $tabPanel.style.setProperty('--index', index < this.activeIndex ? index + 1 : index - 1);
            } else {
                $tabPanel.style.setProperty('--index', index);
            }
        });

        this.$$videos.forEach(($video, index) => {
            if (index === this.activeIndex) {
                $video.removeAttribute(this.settings.hiddenAttr);
                $video.videoAPI.toggleVideoState(this.shouldPlay);
            } else {
                $video.setAttribute(this.settings.hiddenAttr, '');
                $video.videoAPI.toggleVideoState(false);
            }

            $video.setAttribute(this.settings.animateAttr, '');

            window.setTimeout(() => {
                $video.removeAttribute(this.settings.animateAttr);
            }, 750);
        });

        this.updateTheming($activeTabPanel);
    }

    /**
     * Updates the global theming marker state:
     * - Reads `data-tabcontent-theming` and `data-tabcontent-lightness` from the active tab panel
     * - Updates the marker through `window.themingMarkerAPI` if available
     * - Triggers a theming update if the marker is active
     * @param {HTMLElement} $activeTabPanel
     */
    updateTheming ($activeTabPanel) {
        if (window.themingMarkerAPI) {
            const theming = $activeTabPanel.getAttribute(this.settings.themingAttr);
            const lightness = $activeTabPanel.getAttribute(this.settings.lightnessAttr);
            const index = Array.from(window.themingMarkerAPI.$$markers).indexOf(this.$themingMarker);
            window.themingMarkerAPI.updateMarker(index, this.$themingMarker, theming, lightness);

            if (window.themingMarkerAPI.markersState[index].active) {
                window.themingMarkerAPI.updateTheming();
            }
        }
    }

    updateAnimation (updateRequired) {
        this.animation.defaultOptions['animateCircle'].scale = window.reducedMotionActive ? {0: 0.2, 100: 0.2} : {0: 0.2, 100: 1};

        if (updateRequired) {
            this.animation.update();
        }
    }

    /**
     * Sets event listeners on the root element:
     * - `tab_navigation.change`: switches the active tab panel
     * - `media_control.change`: toggles video playback for all videos
     */
    setEvents() {
        window.addEventListener('motionPreferenceChanged', () => {
            this.updateAnimation(true);
        });

        this.$root.addEventListener('tab_navigation.change', (e) => {
            const { id } = e.detail;

            const $activeTabPanel = [...this.$$tabPanels].find(
                ($tabPanel) => $tabPanel.id === id
            );

            this.activeIndex = [...this.$$tabPanels].findIndex(
                ($tabPanel) => $tabPanel.id === id
            );

            this.toggleTabPanels($activeTabPanel);
        });

        this.$root.addEventListener('media_control.change', (event) => {
            this.shouldPlay = !event.detail.pressed;

            this.$root.setAttribute(this.settings.pausedAttr, !this.shouldPlay);

            this.$$videos.forEach($video => {
                $video.videoAPI.toggleVideoState(this.shouldPlay);

                this.$$videos.forEach(($video, index) => {
                    if (this.shouldPlay) {
                        $video.videoAPI.toggleVideoState(index === this.activeIndex);
                    } else {
                        $video.videoAPI.toggleVideoState(false);
                    }
                });
            });
        });
    }
}

export { TabContent };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-tabcontent="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new TabContent($root);
            $rootAPI.initialize();
        });
    }
});
